<template lang="pug">
  div(class="billing-acts")
    div(
      v-if="acts.length"
      class="billing-acts__header"
    )
      div
        h4(class="text-16px fw-500 dark mb-1") {{ $t('Control your financial statements') }}
        div(class="text-12px fw-300") {{ $t('All certificates of work performed in one place - download, save, transfer to accounting') }}
      m-block-table-pagination(
        :meta="meta"
        @prev="prevPage"
        @next="nextPage"
      )
    div(class="billing-acts__table")
      m-table(
        store-name="acts"
        :headers="headers"
        :hide-default-header="!acts.length"
        hide-default-footer
        :empty-block="emptyBlock"
        :item-actions="actions"
        :mobile-item-config="mobileItemConfig"
        ref="table"
      )
        template(v-slot:emptyTableActions)
          v-btn(
            v-if="!$hasProVersion"
            class="main-button"
            :loading="updateToPremiumLoading"
            @click="updateToPremium(false)"
          ) {{ $t('Update to Premium') }}
          v-btn(
            class="secondary-button"
            @click="goBuyDocuments"
          ) {{ $t('Buy documents') }}
</template>

<script>
import { mapGetters } from 'vuex'
import Download from 'js-file-download'
import MTable from '~/components/modules/tables/m-table'
import actions from '~/modules/billing/mixins/actions'
import MBlockTablePagination from '~/components/modules/blocks/concrete/m-block-table-pagination'
import contentDialog from '~/mixins/dialogs/contentDialog'
import converters from '~/mixins/methods/converters'

export default {
  name: 'BillingActs',
  components: {
    MBlockTablePagination,
    MTable
  },
  mixins: [actions, contentDialog, converters],
  data() {
    return {
      loading: false
    }
  },
  computed: {
    ...mapGetters('acts', {
      acts: 'items',
      meta: 'meta'
    }),
    headers() {
      return [
        {
          text: 'Organization',
          value: this.$Organization?.name,
          sortable: false,
          filterable: false
        },
        {
          text: 'Date',
          value: 'date',
          valueFormatter: act => this.$moment.parseZone(act.date).format('DD.MM.YYYY, HH:mm'),
          sortable: false,
          filterable: false
        },
        {
          text: 'Period (from - to)',
          value: 'startDate',
          valueFormatter: act =>
            this.$moment.parseZone(act.startDate).format('DD.MM.YYYY') +
            ' - ' +
            this.$moment.parseZone(act.endDate).format('DD.MM.YYYY'),
          sortable: false,
          filterable: false
        },
        {
          text: 'Sum, uah',
          value: 'sum',
          valueFormatter: act => (act.sum / 100).toFixed(2),
          sortable: false,
          filterable: false,
          align: 'center'
        },
        {
          text: 'Actions',
          align: 'center',
          value: 'actions',
          width: '120',
          sortable: false,
          filterable: false
        }
      ]
    },
    actions() {
      return [
        {
          name: 'viewAndSign',
          text: 'View and sign',
          icon: 'eye',
          call: act => this.viewAndSignAct(act)
        },
        {
          name: 'download',
          text: 'Download signed act',
          icon: 'arrow-download',
          call: act => this.downloadAct(act)
        }
      ]
    },
    emptyBlock() {
      return {
        title: this.$t('Build reporting from day one'),
        subtitle: this.$t(
          'Receive official certificates of work performed automatically after each paid service for reporting purposes'
        ),
        image: 'balance-scale',
        imageExtension: 'gif'
      }
    },
    mobileItemConfig() {
      return {
        title: this.$Organization?.name,
        subtitle: 'date', // todo: valueFormatter
        additionalData: [
          {
            valueFormatter: act =>
              this.$t('Period (from - to)') +
              ': ' +
              this.$moment.parseZone(act.startDate).format('DD.MM.YYYY') +
              ' - ' +
              this.$moment.parseZone(act.endDate).format('DD.MM.YYYY')
          },
          {
            valueFormatter: act => (act.sum / 100).toFixed(2)
          }
        ]
      }
    }
  },
  methods: {
    goBuyDocuments() {
      this.$emit('goToTab', 0)
    },
    async viewAndSignAct(act) {
      const blob = await this.downloadAct(act, 'printable', false)
      const dataUrl = await this.blobToBase64(blob)
      this.contentDialog.open({
        width: 900,
        component: 'billing-act-viewer',
        componentProps: {
          src: dataUrl,
          act
        },
        removePadding: true
      })
    },
    async downloadAct(act, type = 'signed', shouldDownload = true) {
      try {
        const blob = await this.$store.dispatch('acts/downloadAct', { id: act?.id, type })
        if (blob && shouldDownload) {
          Download(blob, `act-${act.number}.pdf`)
        }
        return blob
      } catch (e) {
        this.$handlers.error(e, this)
      }
    },
    async prevPage() {
      await this.tableRequest({ offset: this.meta.currentPage - 1 })
    },
    async nextPage() {
      await this.tableRequest({ offset: this.meta.currentPage + 1 })
    },
    async tableRequest(params) {
      if (this.$refs.table && this._.isFunction(this.$refs.table.fetchData)) {
        await this.$refs.table.fetchData(params)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.billing-acts {
  border-radius: 16px;
  border: 1px solid $border-disabled;
  padding-top: 16px;
  height: 100%;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    padding: 0 16px;
  }

  &__table {
    height: 100%;
  }
}
</style>
